import { useCallback } from 'react';
import { useAgentStore } from '@/stores/agentStore';
import { useReferenceStore } from '@/stores/referenceStore';
import { API_BASE } from '@/config';
import { fileToBase64, summarizeTopic } from '../helpers';
import { Message, AttachmentItem } from '../types';

export function buildUserMessage(input: string, attachments: AttachmentItem[], references: any[]): Message {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    role: 'user',
    text: input,
    attachments: attachments.length > 0 ? attachments : undefined,
    references: references.length > 0
      ? references.map((r: any) => ({ id: r.id, thumbnailUrl: r.thumbnailUrl, title: r.node.title }))
      : undefined,
  };
}

export async function processAttachments(attachments: AttachmentItem[], references: any[]) {
  const attachmentPayloads = await Promise.all(
    attachments.map(async (a) => {
      const base: any = {
        name: a.name,
        type: a.type,
        size: a.size,
        thumbnailUrl: a.thumbnailUrl,
      };
      if (a.file && a.type.startsWith('image/')) {
        try {
          const dataUrl = await fileToBase64(a.file);
          base.dataUrl = dataUrl;
        } catch {
        }
      }
      return base;
    })
  );

  const referencePayloads = references.map((r: any) => {
    const nodeData = r.node.data || {};
    const rawUrl = nodeData.image || nodeData.videoUri || r.thumbnailUrl || '';
    let dataUrl = '';
    let imageUrl = '';
    if (rawUrl.startsWith('data:')) {
      dataUrl = rawUrl;
      imageUrl = rawUrl;
    } else if (rawUrl.startsWith('http')) {
      imageUrl = rawUrl;
    } else if (rawUrl.startsWith('/assets/') || rawUrl.startsWith('/api/')) {
      imageUrl = `${API_BASE}${rawUrl}`;
    }
    return {
      id: r.id,
      title: r.node.title,
      thumbnailUrl: imageUrl || r.thumbnailUrl,
      nodeType: r.node.type,
      dataUrl,
    };
  });

  return { attachmentPayloads, referencePayloads };
}

export function useStreamSession() {
  const prepareSendMessage = useCallback(async () => {
    const store = useAgentStore.getState();
    const currentInput = store.input;
    if (!currentInput.trim() || store.isLoading) return null;

    const { useProjectStore: _useProjectStore } = await import('@/stores/projectStore');
    const currentProjectId = _useProjectStore.getState().currentProjectId;
    if (currentProjectId) {
      await store.ensureSession(currentProjectId);
    }

    const currentSessionId = useAgentStore.getState().sessionId;
    const currentMessages = useAgentStore.getState().messages;
    if (currentSessionId) {
      if (currentMessages.length === 0) {
        const optimisticEntry = {
          id: currentSessionId,
          topic: summarizeTopic(currentInput.trim()) || '未命名对话',
          date: new Date().toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
          updatedAt: new Date().toISOString(),
          messages: [],
        };
        useAgentStore.getState().setChatHistory(prev => [optimisticEntry, ...prev]);
      }
      useAgentStore.getState().setSelectedHistoryId(currentSessionId);
    }

    return { input: currentInput, sessionId: currentSessionId, messages: currentMessages, projectId: currentProjectId };
  }, []);

  const buildContextPrefix = useCallback((currentRefs: any[], currentAttachments: any[], userText: string) => {
    let contextPrefix = '';
    if (currentRefs.length > 0) {
      const refDescs = currentRefs.map((r: any) => r.node.title || r.id).join(', ');
      contextPrefix = `[用户引用了以下参考素材: ${refDescs}] `;
    }
    if (currentAttachments.length > 0) {
      const attDescs = currentAttachments.map((a: any) => `${a.file?.name || a.name}(${a.file?.type || a.type})`).join(', ');
      contextPrefix += `[用户上传了附件: ${attDescs}] `;
    }
    return contextPrefix + userText;
  }, []);

  const initStreamState = useCallback((userText: string, hasAttachments: boolean, hasReferences: boolean, initialIntro: string) => {
    const s = useAgentStore.getState();
    s.setIsLoading(true);
    s.setIsThinking(false);
    s.setThinkingSeconds(0);
    s.startThinkingTimer();
    s.setStreamingText(null);
    s.setCurrentIntent('chat');
    s.setActiveProcessLogs([]);
    s.setActiveAssistantIntro(initialIntro);
    // 🔵 重置流式会话结束标志，确保新一轮流式会话中搜索/确认回调可以正常初始化 builder
    useAgentStore.setState({ streamEnded: false });
  }, []);

  return { prepareSendMessage, buildContextPrefix, initStreamState };
}
